import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, KeyRound, Plus, Copy, Check, Trash2, AlertTriangle, Plug } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import './AccessTokens.css';

/*
 * Personal access tokens (WR-012 A1).
 *
 * A token is a second way for you to be you, aimed at machines: an MCP
 * client, a script, anything that cannot sign in with a password. Scoped to
 * the account, not to an organization or project — it inherits exactly the
 * access the account already has.
 *
 * The plaintext exists once, in the response to the create call. There is no
 * route that can return it again, because the server keeps only a hash. That
 * is why the freshly-created token gets a panel of its own rather than a row
 * in the list.
 *
 * ── Connected apps (WR-012 Phase B) ──────────────────────────────────
 *
 * The same modal also lists OAuth connections: claude.ai and anything else
 * that completed the authorization code flow. They are a SEPARATE list from
 * the tokens above, deliberately.
 *
 * A token is something you made and must keep safe. A connection is
 * something you approved and can withdraw. They are revoked for different
 * reasons — "I lost the laptop" versus "I no longer use that assistant" —
 * and one undifferentiated list would make you work out which row is which
 * before you could act. Their access is also narrower: a connection carries
 * only the scopes shown on the consent screen, where a token carries the
 * whole account.
 *
 * Shell mirrors the Completed view (CompletedTasks.js): header band,
 * scrolling content, rows as bordered cards.
 */

function AccessTokens({ onClose }) {
  const [tokens, setTokens]   = useState([]);
  const [grants, setGrants]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(false);

  const [newName, setNewName] = useState('');
  // The one-time reveal. Cleared when dismissed and unrecoverable after that.
  const [revealed, setRevealed] = useState(null);
  const [copied, setCopied]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // One round trip each; a failure in either must not blank the other.
      const [tok, grt] = await Promise.all([axios.get('/tokens'), axios.get('/tokens/grants')]);
      setTokens(tok.data);
      setGrants(grt.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load your tokens.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post('/tokens', { name });
      setRevealed(res.data.token);
      setCopied(false);
      setNewName('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create that token.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (token) => {
    const ok = window.confirm(
      `Revoke "${token.name}"?\n\n` +
      'Anything using this token stops working immediately. This cannot be undone — ' +
      'you would need to create a new token and reconfigure the client.'
    );
    if (!ok) return;

    setBusy(true);
    setError(null);
    try {
      await axios.delete(`/tokens/${token._id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not revoke that token.');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (grant) => {
    const ok = window.confirm(
      `Disconnect "${grant.clientName}"?\n\n` +
      'It stops being able to act in Bastion immediately. You can connect it again ' +
      'later, which will ask you to approve it afresh.'
    );
    if (!ok) return;

    setBusy(true);
    setError(null);
    try {
      await axios.delete(`/tokens/grants/${grant._id}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not disconnect that app.');
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(revealed);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied — the token is on screen and selectable,
      // so this is a convenience failing, not the flow failing.
      setError('Could not copy automatically — select the token and copy it by hand.');
    }
  };

  const active  = tokens.filter(t => !t.revokedAt);
  const revoked = tokens.filter(t => t.revokedAt);
  // Revoked connections are dropped rather than greyed out: unlike a token,
  // a connection has no name you chose and nothing to recognise later, so
  // keeping the corpse on screen is clutter, not history.
  const connected = grants.filter(g => !g.revokedAt);

  const SCOPE_WORDS = {
    'mcp:read':  'read your work',
    'mcp:write': 'create and update tasks',
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content pat-modal" onClick={e => e.stopPropagation()}>
        <div className="pat-header">
          <div className="pat-title">
            <KeyRound size={20} />
            <div>
              <h2>Access tokens</h2>
              <p>
                {loading
                  ? 'Loading…'
                  : `${active.length} active${revoked.length ? ` · ${revoked.length} revoked` : ''}`
                    + `${connected.length ? ` · ${connected.length} connected app${connected.length > 1 ? 's' : ''}` : ''}`}
              </p>
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="pat-content">
          {error && <div className="pat-error" role="alert">{error}</div>}

          {revealed && (
            <div className="pat-reveal" role="alert">
              <div className="pat-reveal-head">
                <AlertTriangle size={16} />
                <strong>Copy this now — it will not be shown again</strong>
              </div>
              <p>
                Bastion stores only a hash of this token, so there is no way to
                display it a second time. If you lose it, revoke it and create another.
              </p>
              <div className="pat-reveal-row">
                <code className="pat-token">{revealed}</code>
                <button className="btn btn-primary btn-sm pat-copy" onClick={copy}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setRevealed(null)}>
                I've saved it
              </button>
            </div>
          )}

          {!loading && tokens.length === 0 && !revealed && (
            <div className="pat-empty">
              <KeyRound size={32} />
              <h3>No tokens yet</h3>
              <p>
                Create one to let an AI assistant or a script act in Bastion as you.
                Tokens can read and change your projects, but cannot change your
                password or create further tokens.
              </p>
            </div>
          )}

          {tokens.length > 0 && (
            <ul className="pat-list">
              {tokens.map(token => (
                <li key={token._id} className={`pat-row${token.revokedAt ? ' is-revoked' : ''}`}>
                  <KeyRound size={16} className="pat-row-icon" />
                  <div className="pat-row-main">
                    <span className="pat-row-title">
                      {token.name}
                      {token.revokedAt && <span className="pat-badge">Revoked</span>}
                    </span>
                    <div className="pat-row-meta">
                      <code>{token.prefix}…</code>
                      <span title={format(new Date(token.createdAt), 'PPpp')}>
                        Created {format(new Date(token.createdAt), 'MMM d, yyyy')}
                      </span>
                      <span>
                        {token.lastUsedAt
                          ? `Last used ${formatDistanceToNow(new Date(token.lastUsedAt), { addSuffix: true })}`
                          : 'Never used'}
                      </span>
                      {token.revokedAt && (
                        <span title={format(new Date(token.revokedAt), 'PPpp')}>
                          Revoked {format(new Date(token.revokedAt), 'MMM d, yyyy')}
                        </span>
                      )}
                    </div>
                  </div>
                  {!token.revokedAt && (
                    <button
                      className="btn btn-ghost btn-sm pat-action"
                      onClick={() => revoke(token)}
                      disabled={busy}
                    >
                      <Trash2 size={14} />
                      Revoke
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="pat-row pat-add">
            <Plus size={16} className="pat-row-icon" />
            <div className="pat-row-main">
              <input
                className="pat-input"
                placeholder="What is this token for? e.g. Claude Code on laptop"
                value={newName}
                maxLength={60}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') create(); }}
              />
              <div className="pat-row-meta">
                <span>Name it after the machine or tool, so you know what you are revoking later.</span>
              </div>
            </div>
            <button
              className="btn btn-primary btn-sm pat-action"
              onClick={create}
              disabled={busy || !newName.trim()}
            >
              <Plus size={14} />
              Create
            </button>
          </div>

          {connected.length > 0 && (
            <>
              <h3 className="pat-section">Connected apps</h3>
              <p className="pat-section-note">
                Apps you approved through Bastion's sign-in screen. They act as you,
                limited to the permissions you approved.
              </p>
              <ul className="pat-list">
                {connected.map(grant => (
                  <li key={grant._id} className="pat-row">
                    <Plug size={16} className="pat-row-icon" />
                    <div className="pat-row-main">
                      <span className="pat-row-title">{grant.clientName}</span>
                      <div className="pat-row-meta">
                        <span>
                          Can {(grant.scopes || [])
                            .map(s => SCOPE_WORDS[s] || s)
                            .join(' and ') || 'do nothing'}
                        </span>
                        <span title={format(new Date(grant.createdAt), 'PPpp')}>
                          Connected {format(new Date(grant.createdAt), 'MMM d, yyyy')}
                        </span>
                        <span>
                          {grant.lastUsedAt
                            ? `Last used ${formatDistanceToNow(new Date(grant.lastUsedAt), { addSuffix: true })}`
                            : 'Not used yet'}
                        </span>
                      </div>
                    </div>
                    <button
                      className="btn btn-ghost btn-sm pat-action"
                      onClick={() => disconnect(grant)}
                      disabled={busy}
                    >
                      <Trash2 size={14} />
                      Disconnect
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccessTokens;
